import {ArchiveRecord} from 'archive/ArchiveConstants';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {Status as AutomationFileStatus} from 'constants/StatusConstants';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LabAutomationTransformType} from 'lab-automation/settings/RunSchemaSpecConstants';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {PlateRecord} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';

import {FileGraphQL} from '../../../graphql-fragments/files/__generated/FileGraphQL';
import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';

export interface AutomationInputFileConfig {
  id: number;
  name: string;
  isHidden?: boolean;
}

// Keep in sync with benchling/lab_automation/enums.py:AutomationOutputFileActionType
export enum AutomationOutputFileActionType {
  ENTITY_REGISTRATION = 'ENTITY_REGISTRATION',
  PURE_TRANSFER = 'PURE_TRANSFER',
  RESULTS_UPLOAD = 'RESULTS_UPLOAD',
  TRANSFORMATION_TRANSFER = 'TRANSFORMATION_TRANSFER',
}

export interface AutomationOutputFileConfig {
  id: number;
  name: string;
  tagSchemaInfo?: {
    tagSchemaId: string;
    // Not present in configs versioned before these fields were added to the to_dict
    folderId?: string | null;
    sourceId?: string | null;
  } | null;
  destinationInfo?: {
    sourceId: string | null;
    storableSchemaId: string;
    locationId: string | null;
  } | null;
  resultSchemaInfo?: {
    resultSchema: ResultSchema | null;
  } | null;
  isHidden?: boolean;
  automationOutputFileActionType?: AutomationOutputFileActionType | null;
  shouldCreateDataset?: boolean;
  shouldAllowAttachment?: boolean;
  shouldPerformPlateTransfer?: boolean;
  shouldRecordImages?: boolean;
  spreadsheetProcessingLocale?: string;
  transformDataType?: string | null;
  jsonMapperConfigJson?: string | null;
  canConfigureTimezone?: boolean;
}

export type AutomationFileErrorInfo =
  | {topLevelErrors: Array<{errorMessage: string}>}
  | SpreadsheetValidationErrorInfo;

interface BaseAutomationInputFile {
  automationInputFileConfig: AutomationInputFileConfig;
  modifiedAt?: string;
  transformFiles?: Array<AutomationTransformFile | null>;
}

export interface SucceededAutomationInputFile extends BaseAutomationInputFile {
  id: string;
  status: AutomationFileStatus.SUCCEEDED;
  blob: BenchlingBlob;
  errors: null;
}

export interface FailedAutomationInputFile extends BaseAutomationInputFile {
  id: string | null;
  status: AutomationFileStatus.FAILED;
  blob: null;
  errors: AutomationFileErrorInfo;
}

export interface PendingOrRunningAutomationInputFile extends BaseAutomationInputFile {
  id: string | null;
  status: AutomationFileStatus.PENDING | AutomationFileStatus.RUNNING;
  blob: null;
  errors: null;
}

export type AutomationInputFile =
  | PendingOrRunningAutomationInputFile
  | SucceededAutomationInputFile
  | FailedAutomationInputFile;

export interface AutomationTransformFile {
  id: string;
  status: AutomationFileStatus;
  blob: BenchlingBlob | null;
  type: LabAutomationTransformType;
  errors: AutomationFileErrorInfo | null;
  modifiedAt?: string;
}

// Keep in sync with AutomationOutputFile.AutomationOutputFileType
export enum AutomationOutputFileType {
  OUTPUT_FILE = 'OUTPUT_FILE',
  AMENDED_ERRORS_FILE = 'AMENDED_ERRORS_FILE',
}

export interface ProcessingSummaryStatistics {
  numRowsProcessed: number;
  numRowsFailed: number;
  totalRows: number;
  percentOfRowsProcessed: number;
}

export interface AutomationOutputFile {
  id: string;
  runId: string;
  status: AutomationFileStatus;
  blob: BenchlingBlob | null;
  automationOutputFileConfig: AutomationOutputFileConfig;
  errors: AutomationFileErrorInfo | null;
  folderId?: string | null;
  locationId?: string | null;
  archiveRecord?: ArchiveRecord | null;
  canConfigureTimezone?: boolean;
  skipRowsWithErrors?: boolean;
  timezone?: string | null;
  transforms?: Array<AutomationTransformFile | null>;
  numRowsWithErrors?: number;
  rowLevelErrorsBlob?: BenchlingBlob | null;
  createdAt?: string;
  modifiedAt?: string;
  automationOutputFileType?: AutomationOutputFileType;
  skipRowsWithErrorsRequired?: boolean;
  datasetId?: string | null;
  analysisId?: string | null;
  plateRecords?: Array<PlateRecord>;
  name: string;
  createdFile: FileGraphQL | null;
}

export interface AutomationOutputFileSpec {
  configName: string;
  blobId: string | null;
}

export interface AutomationErrorFileWithBlob {
  id: string;
  status: AutomationFileStatus.SUCCEEDED;
  blob: BenchlingBlob;
  automationOutputFileConfig: AutomationOutputFileConfig;
}
export interface AutomationErrorFileWithoutBlob {
  id: string | null;
  status: AutomationFileStatus.PENDING;
  blob: null;
  automationOutputFileConfig: AutomationOutputFileConfig;
}

export type AutomationErrorFile = AutomationErrorFileWithBlob | AutomationErrorFileWithoutBlob;

export type AutomationInputTransformFileGraphQL = NonNullable<
  NonNullable<AutomationInputFileGraphQL['transformFiles']>[number]
>;

export type AutomationOutputTransformFileGraphQL = NonNullable<
  NonNullable<AutomationOutputFileGraphQL['transforms']>[number]
>;

export type AutomationTransformFileGraphQL =
  | AutomationInputTransformFileGraphQL
  | AutomationOutputTransformFileGraphQL;

export const AUTOMATION_FILE_STATUS_TO_GRAPHQL_MAP: Record<
  AutomationFileStatus,
  AutomationFileStatusGraphQL
> = {
  [AutomationFileStatus.PENDING]: AutomationFileStatusGraphQL.pending,
  [AutomationFileStatus.RUNNING]: AutomationFileStatusGraphQL.running,
  [AutomationFileStatus.SUCCEEDED]: AutomationFileStatusGraphQL.succeeded,
  [AutomationFileStatus.FAILED]: AutomationFileStatusGraphQL.failed,
};

export const AUTOMATION_FILE_STATUS_FROM_GRAPHQL_MAP: Record<
  AutomationFileStatusGraphQL,
  AutomationFileStatus
> = {
  [AutomationFileStatusGraphQL.pending]: AutomationFileStatus.PENDING,
  [AutomationFileStatusGraphQL.running]: AutomationFileStatus.RUNNING,
  [AutomationFileStatusGraphQL.succeeded]: AutomationFileStatus.SUCCEEDED,
  [AutomationFileStatusGraphQL.failed]: AutomationFileStatus.FAILED,
};
